'use client';

import React, { useState } from 'react';
import { cm } from 'src/utils/classNames';
import { BigNum, BN } from '@velocity-exchange/sdk';
import SkeletonValuePlaceholder from './SkeletonValuePlaceholder/SkeletonValuePlaceholder';
import { ZERO_BIGNUM } from 'src/constants/math';
import { LoadingChart } from './Charts/LoadingChart';
import { useRateHistory } from 'src/hooks/lendAndBorrow/useRateHistory';
import { HistoricalTokenPriceDuration } from 'src/utils/graph';
import PnlChart from './PnlChart';
import Tabs from 'src/components/primitives/Tabs/Tabs';
import NumberDisplay from './NumberDisplay/NumberDisplay';

const dateRangeOptions = [
	{ label: '7d', value: HistoricalTokenPriceDuration.ONE_WEEK },
	{ label: '30d', value: HistoricalTokenPriceDuration.ONE_MONTH },
	{ label: '90d', value: HistoricalTokenPriceDuration.NINETY_DAYS },
];

export const RateHistoryChart = (props: {
	marketIndex: number;
	type: 'deposit' | 'borrow';
	showAverage?: boolean;
	averageLabelText?: 'string';
	className?: string;
	chartClassName?: string;
	loadingClassName?: string;
}) => {
	const displayDecimals = 5;
	const [dateRange, setDateRate] = useState<HistoricalTokenPriceDuration>(
		HistoricalTokenPriceDuration.NINETY_DAYS
	);
	const { data, isLoading, error } = useRateHistory({
		marketIndex: props.marketIndex,
		type: props.type,
		dateRange,
	});

	const { chartData, averageRate: averageRateApy } = data ?? {};

	const handleChangeDateRange = (range: HistoricalTokenPriceDuration) => {
		if (isLoading) return;
		setDateRate(range);
	};

	const capitalizedType = props.type === 'deposit' ? 'Deposit' : 'Borrow';
	const chartLabel = `${capitalizedType} Rate`;

	return (
		<div>
			<div className="flex flex-row items-center justify-between">
				<div className="flex gap-1">
					<span className="typo-body-xs text-text-secondary">
						{props.averageLabelText || 'Avg APY'}
					</span>
					{props.showAverage &&
						(isLoading ? (
							<SkeletonValuePlaceholder className="mt-1 h-3 w-12" loading />
						) : (
							<NumberDisplay
								value={
									(averageRateApy &&
										!isNaN(averageRateApy) &&
										BigNum.fromPrint(
											`${Math.round(averageRateApy * 10000) / 10000}`,
											new BN(4)
										)) ||
									ZERO_BIGNUM
								}
								type="percentage"
								decimals={4}
								signDependentColor
								className="typo-label-sm"
							/>
						))}
				</div>
				<Tabs
					variant="secondary"
					size="sm"
					options={dateRangeOptions}
					activeTab={dateRange}
					onTabChange={handleChangeDateRange}
					className={cm('w-fit', isLoading ? 'opacity-50' : '')}
				/>
			</div>
			<div
				className={cm(
					'mt-4 mb-4 min-h-[200px] w-full grow items-center justify-center',
					props.className
				)}
			>
				{isLoading ? (
					<div
						className={cm(
							`flex h-[200px] w-full flex-col items-center justify-center rounded-sm text-center`,
							props.loadingClassName
						)}
					>
						<LoadingChart />
					</div>
				) : error ? (
					<div
						className={cm(
							`flex h-[200px] w-full flex-col items-center justify-center rounded-sm p-6 text-center`,
							props.loadingClassName
						)}
					>
						<span className="typo-body-xs text-text-secondary">
							Unable to fetch {props.type} rate history
						</span>
					</div>
				) : (
					<PnlChart
						id={`rate-history-${props.type}`}
						className={cm('min-h-[200px]', props.chartClassName)}
						data={chartData ?? []}
						field={'totalPnl'}
						label={chartLabel}
						decimalsToShow={displayDecimals}
						positive={false}
						isHourly={false}
						overrideNoBeginAtZero
						noYearInXAxis
						nonNotional
						isInModal
						isPercent
					/>
				)}
			</div>
		</div>
	);
};
